import { createShapeNormalizer } from '@change-corgi/fcm/normalizers';
import type { Normalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';

type NewsAndGuidesVisibility = Readonly<Partial<Record<string, 'short' | 'long'>>>;

const normalizeShape = createShapeNormalizer({
	type: 'record',
	shape: { type: 'enum', values: ['short', 'long'] as const },
	optionalValues: true,
});

/**
 * Normalizes the shape, then rejects any country-code key that isn't uppercase — consumers look
 * up by `countryCode.toUpperCase()`, so this enforces a consistent format rather than silently
 * coercing it.
 */
const normalizeNewsAndGuidesVisibility: Normalizer<NewsAndGuidesVisibility> = (value: unknown) => {
	const shape = normalizeShape(value);
	for (const countryCode of Object.keys(shape)) {
		if (countryCode !== countryCode.toUpperCase()) {
			throw new Error(`News and guides visibility country code "${countryCode}" must be uppercase`);
		}
	}
	return Object.fromEntries(Object.entries(shape).filter(([, mode]) => mode));
};

export const FCM_NEWS_AND_GUIDES_VISIBILITY = defineFcm('news_and_guides_visibility')
	.asJson(normalizeNewsAndGuidesVisibility)
	.withDefault({})
	.toConfig({
		owner: 'squad-narwhal',
		scope: 'corgi-front-end',
		context: 'News and Guides (/l/:countryCode and /l/:countryCode/:slug)',
		description: `
			Controls per-country visibility of the News and Guides blog. Set globally — not per-country.

			Map of uppercase country code to disable mode. Countries omitted from the map are enabled (default).

			- "short" issues an HTTP 307 (temporary; for disables of a few weeks up to a few months — search engines keep the URLs indexed).
			- "long" issues an HTTP 301 (permanent; for multi-month/year disables, so search engines drop the URLs).

			Effects when a country is in the map:
			- /l/:countryCode and /l/:countryCode/:slug redirect to /petition-guides (regardless of who's visiting).
			- The footer "Blog" link is hidden for users whose session country matches a key in the map.
		`,
		supportedGroups: { global: true },
		template: { BV: 'long', HM: 'short' },
	});
